import React from 'react';
import { Users, TrendingUp, Award, Calendar, Receipt } from 'lucide-react';

const fmt = (n) =>
  parseFloat(n || 0).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function StatCard({ label, value, sub, Icon, iconCls }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-xs text-gray-400 font-medium">{label}</p>
          <p className="text-xl font-bold text-gray-900 mt-1">{value}</p>
          {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
        </div>
        <div className={`p-2 rounded-lg ${iconCls}`}>
          <Icon size={16} />
        </div>
      </div>
    </div>
  );
}

const METODO_LABELS = {
  antiguedad:         '45/60 Días por Antigüedad',
  monto_global:       'Monto Global',
  cantidad_salarios:  'N × Salario',
  monto_por_empleado: 'Monto Fijo / Empleado',
};
const DIST_LABELS = {
  proporcional: 'Proporcional al salario',
  igualitario:  'Igualitario',
  desempeno:    'Por desempeño',
};

export default function ResumenTab({ bonificacion: b, detalle }) {
  const activos    = detalle.filter(d => d.estado !== 'excluido');
  const excluidos  = detalle.filter(d => d.estado === 'excluido');
  const promedio   = activos.length > 0
    ? parseFloat(b.total_bonificacion || 0) / activos.length
    : 0;

  // Distribución por tramos de antigüedad
  const tramos = [
    { label: '< 1 año',     min: 0,  max: 1  },
    { label: '1–3 años',    min: 1,  max: 3  },
    { label: '3–5 años',    min: 3,  max: 5  },
    { label: '5–10 años',   min: 5,  max: 10 },
    { label: '> 10 años',   min: 10, max: Infinity },
  ].map(t => ({
    ...t,
    count:  activos.filter(d => parseFloat(d.anos_servicio) >= t.min && parseFloat(d.anos_servicio) < t.max).length,
    monto:  activos.filter(d => parseFloat(d.anos_servicio) >= t.min && parseFloat(d.anos_servicio) < t.max)
                   .reduce((s, d) => s + parseFloat(d.monto_final || 0), 0),
  })).filter(t => t.count > 0);

  const maxMonto = Math.max(...tramos.map(t => t.monto), 1);

  return (
    <div className="space-y-5">

      {/* Stat cards */}
      <div className={`grid gap-3 ${b.isr_calculado ? 'grid-cols-2 lg:grid-cols-5' : 'grid-cols-2 lg:grid-cols-4'}`}>
        <StatCard
          label="Total Empleados"
          value={activos.length}
          sub={excluidos.length > 0 ? `${excluidos.length} excluido(s)` : undefined}
          Icon={Users}
          iconCls="bg-blue-50 text-blue-600"
        />
        <StatCard
          label="Total Bonificación"
          value={`RD$ ${fmt(b.total_bonificacion)}`}
          Icon={Award}
          iconCls="bg-yellow-50 text-yellow-600"
        />
        {b.isr_calculado && (
          <StatCard
            label="ISR Marginal Total"
            value={`RD$ ${fmt(b.total_isr)}`}
            sub="ISR por bonificación"
            Icon={Receipt}
            iconCls="bg-orange-50 text-orange-600"
          />
        )}
        <StatCard
          label="Promedio por Empleado"
          value={`RD$ ${fmt(promedio)}`}
          Icon={TrendingUp}
          iconCls="bg-green-50 text-green-600"
        />
        <StatCard
          label="Año Fiscal"
          value={b.anio}
          sub={`Tipo: ${b.tipo}`}
          Icon={Calendar}
          iconCls="bg-violet-50 text-violet-600"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        {/* Configuración */}
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Configuración</p>
          <div className="space-y-2">
            {(b.tipo === 'LEGAL' || b.tipo === 'MIXTA') && (
              <>
                <ConfigRow label="Utilidad neta"       value={`RD$ ${fmt(b.utilidad_neta)}`} />
                <ConfigRow label="% Utilidad"          value={`${b.porcentaje_utilidad}%`} />
                <ConfigRow label="Monto a distribuir"  value={`RD$ ${fmt(b.monto_a_distribuir)}`} bold />
                <ConfigRow label="Topes legales"       value={b.aplicar_topes ? 'Sí (45/60 días)' : 'No'} />
              </>
            )}
            {(b.tipo === 'FIJA' || b.tipo === 'MIXTA') && (
              <>
                <ConfigRow label="Método"        value={METODO_LABELS[b.metodo_calculo] || b.metodo_calculo} />
                {b.metodo_calculo === 'monto_global' && (
                  <>
                    <ConfigRow label="Monto global"   value={`RD$ ${fmt(b.monto_global)}`} bold />
                    <ConfigRow label="Distribución"   value={DIST_LABELS[b.metodo_distribucion] || b.metodo_distribucion} />
                  </>
                )}
                {b.metodo_calculo === 'cantidad_salarios' && (
                  <ConfigRow label="N° salarios" value={b.cantidad_salarios} bold />
                )}
                {b.metodo_calculo === 'monto_por_empleado' && (
                  <ConfigRow label="Monto por empleado" value={`RD$ ${fmt(b.monto_por_empleado)}`} bold />
                )}
              </>
            )}
            <ConfigRow label="Incluye inactivos" value={b.incluir_inactivos ? 'Sí' : 'No'} />
          </div>
        </div>

        {/* Distribución por antigüedad */}
        {tramos.length > 0 && (
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Distribución por Antigüedad</p>
            <div className="space-y-2.5">
              {tramos.map(t => (
                <div key={t.label}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-gray-600">{t.label}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-400">{t.count} emp.</span>
                      <span className="text-xs font-semibold text-gray-800">RD$ {fmt(t.monto)}</span>
                    </div>
                  </div>
                  <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-500 rounded-full transition-all"
                      style={{ width: `${(t.monto / maxMonto) * 100}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Desglose MIXTA */}
      {b.tipo === 'MIXTA' && (
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center">
            <p className="text-xs text-blue-600 font-medium mb-1">Componente Legal</p>
            <p className="text-lg font-bold text-blue-700">RD$ {fmt(b.total_legal)}</p>
            <p className="text-xs text-blue-500 mt-0.5">
              {parseFloat(b.total_bonificacion) > 0
                ? `${((parseFloat(b.total_legal) / parseFloat(b.total_bonificacion)) * 100).toFixed(1)}%`
                : '—'}
            </p>
          </div>
          <div className="bg-violet-50 border border-violet-200 rounded-xl p-4 text-center">
            <p className="text-xs text-violet-600 font-medium mb-1">Bono Adicional</p>
            <p className="text-lg font-bold text-violet-700">RD$ {fmt(b.total_adicional)}</p>
            <p className="text-xs text-violet-500 mt-0.5">
              {parseFloat(b.total_bonificacion) > 0
                ? `${((parseFloat(b.total_adicional) / parseFloat(b.total_bonificacion)) * 100).toFixed(1)}%`
                : '—'}
            </p>
          </div>
        </div>
      )}

    </div>
  );
}

function ConfigRow({ label, value, bold }) {
  return (
    <div className="flex items-center justify-between py-1 border-b border-gray-100 last:border-0">
      <span className="text-xs text-gray-500">{label}</span>
      <span className={`text-xs ${bold ? 'font-bold text-gray-900' : 'font-medium text-gray-700'}`}>{value}</span>
    </div>
  );
}
